import React from 'react';
import { motion } from 'framer-motion';
import ScrollAnimation from './ScrollAnimation';
import networkAppImage from '../assets/networkApp.jpg';
import mailAppImage from '../assets/mailApp.jpg';
import auctionAppImage from '../assets/auctionApp.jpg';
import wikiAppImage from '../assets/wikiApp.jpg';
import stylegenaiImage from '../assets/square_logo.png';

const projects = [
  {
    title: 'StylegenAI',
    description: 'My first big product: an AI-powered style generation and closet management tool built as a founder-led 0→1 project.',
    image: stylegenaiImage,
    href: 'https://stylegenai.com',
    tags: ['AI', 'Mobile App', 'iOS', 'Android'],
    featured: true,
    sublabel: 'Featured Product',
  },
  {
    title: 'Social Network App',
    description: 'Twitter clone built with React for the frontend and Django for the backend. Features include posts, follows, and likes.',
    image: networkAppImage,
    href: 'https://github.com/ibrahimdemir792/cs50w/tree/main/week4_sql/commerce',
    tags: ['React', 'Django', 'PostgreSQL'],
  },
  {
    title: 'Mail App',
    description: 'Full-featured email client with inbox, sent, and archive functionality. Built with modern web technologies.',
    image: mailAppImage,
    href: '#',
    tags: ['React', 'Django', 'REST API'],
  },
  {
    title: 'Auction App',
    description: 'An e-commerce auction platform where users can create listings, place bids, and manage their watchlist.',
    image: auctionAppImage,
    href: '#',
    tags: ['Django', 'HTML/CSS', 'SQLite'],
  },
  {
    title: 'Wiki App',
    description: 'Wikipedia-inspired encyclopedia with markdown support, search functionality, and random page feature.',
    image: wikiAppImage,
    href: '#',
    tags: ['Django', 'HTML/CSS', 'Markdown'],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 60, rotateX: -15 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: {
      type: 'spring',
      stiffness: 80,
      damping: 15,
    },
  },
};

const Projects = () => (
  <ScrollAnimation>
    <motion.section
      id="projects"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
    >
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Products & Projects
      </motion.h2>

      <motion.div
        className="grid-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        style={{ perspective: '1000px' }}
      >
        {projects.map((project, index) => (
          <motion.div
            key={project.title}
            className="card project-card"
            variants={cardVariants}
            whileHover={{
              y: -15,
              scale: 1.02,
              rotateY: 2,
              transition: { type: 'spring', stiffness: 300, damping: 20 },
            }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Image container with overlay */}
            <motion.div
              style={{
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {project.image ? (
                <motion.img
                  src={project.image}
                  alt={project.title}
                  className="card-image"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                />
              ) : (
                <motion.div
                  className="card-image"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    padding: '1.25rem',
                    background:
                      'radial-gradient(circle at 30% 20%, rgba(0, 212, 255, 0.35) 0%, transparent 55%), radial-gradient(circle at 80% 30%, rgba(124, 58, 237, 0.35) 0%, transparent 55%), linear-gradient(135deg, rgba(26, 26, 37, 0.9), rgba(10, 10, 15, 0.9))',
                  }}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.5 }}
                >
                  <div>
                    {project.sublabel ? (
                      <div
                        style={{
                          fontSize: '0.75rem',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.7)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {project.sublabel}
                      </div>
                    ) : null}
                    <div
                      style={{
                        fontFamily: 'Syne, sans-serif',
                        fontWeight: 800,
                        fontSize: '1.6rem',
                        lineHeight: 1.1,
                        background: 'linear-gradient(135deg, var(--aurora-1), var(--aurora-2), var(--aurora-3))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {project.title}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(10,10,15,0.35)',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'rgba(255,255,255,0.85)',
                      fontWeight: 800,
                    }}
                    aria-hidden="true"
                  >
                    SG
                  </div>
                </motion.div>
              )}
              {/* Gradient overlay */}
              <motion.div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, transparent 50%, rgba(10, 10, 15, 0.9) 100%)',
                  pointerEvents: 'none',
                }}
              />
              {/* Shimmer effect on hover */}
              <motion.div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.1) 45%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.1) 55%, transparent 60%)',
                  transform: 'translateX(-100%)',
                }}
                whileHover={{
                  transform: 'translateX(100%)',
                  transition: { duration: 0.6 },
                }}
              />
            </motion.div>

            <div className="card-content">
              {project.featured ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'center',
                    marginBottom: '0.75rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'clamp(0.65rem, 2vw, 0.75rem)',
                      padding: '0.2rem 0.5rem',
                      background: 'rgba(124, 58, 237, 0.15)',
                      border: '1px solid rgba(124, 58, 237, 0.3)',
                      borderRadius: '999px',
                      color: 'var(--aurora-2)',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                    }}
                  >
                    Featured
                  </span>
                  <span style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)', color: 'var(--text-muted)' }}>
                    Founder-led product
                  </span>
                </div>
              ) : null}
              <motion.h3
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.3 }}
              >
                {project.title}
              </motion.h3>

              <p>{project.description}</p>

              {/* Tags */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.375rem',
                  marginBottom: '1rem',
                }}
              >
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: 'clamp(0.65rem, 2vw, 0.75rem)',
                      padding: '0.2rem 0.5rem',
                      background: 'rgba(0, 212, 255, 0.1)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '100px',
                      color: 'var(--aurora-1)',
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <motion.a
                href={project.href}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{
                  scale: 1.05,
                  x: 5,
                }}
                whileTap={{ scale: 0.95 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span>View Project</span>
              </motion.a>
            </div>

            {/* Glow border effect */}
            <motion.div
              style={{
                position: 'absolute',
                inset: -1,
                background: 'linear-gradient(135deg, var(--aurora-1), var(--aurora-2), var(--aurora-3))',
                borderRadius: '21px',
                zIndex: -1,
                opacity: 0,
              }}
              whileHover={{ opacity: 0.5 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  </ScrollAnimation>
);

export default Projects;
